/**
 * Region API Client
 * 
 * API-Methoden für Regionen & Lizenzen
 */

import apiClient from './api';

export interface Region {
  id: string;
  name: string;
  /** Kategorie für Spalten-Gruppierung: europa | suedeuropa | exotic | event */
  category?: 'europa' | 'suedeuropa' | 'exotic' | 'event';
  description?: string;
  /** Event-Region (zeitlich begrenzte Sonderregion) */
  is_event?: boolean;
  event_start?: string | null;
  event_end?: string | null;
  event_description?: string | null;
  price: number;
  currency: 'money' | 'points';
  is_available: boolean;
  created_at: string;
  updated_at: string;
  /** Reisedauer Standard (Minuten) */
  travel_duration_minutes?: number;
  /** Drei wählbare Suchdauer-Optionen (Minuten) */
  duration_option_1?: number | null;
  duration_option_2?: number | null;
  duration_option_3?: number | null;
  /** Zoll & Lizenzen: Region erfordert Lizenz */
  license_required?: boolean;
  license_price?: number;
  license_duration_days?: number;
  has_customs_risk?: boolean;
  customs_fine_amount?: number;
  customs_catch_probability?: number;
  /** Für /customs: freigeschaltet (Verfügbare) vs. gesperrt (Gesperrte Regionen) */
  is_unlocked?: boolean;
  /** Bild-Pfad für Region-Card (z. B. /images/uploads/xxx.jpg) */
  image_path?: string | null;
}

export interface License {
  id: string;
  user_id: string;
  region_id: string;
  purchased_at: string;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Alle Regionen abrufen
 */
export const getRegions = async (): Promise<{ regions: Region[]; total: number }> => {
  const { data } = await apiClient.get<{ success: boolean; data?: { regions: Region[]; total: number }; error?: { message?: string } }>('/regions');
  if (data?.success && data?.data) return data.data;
  throw new Error(data?.error?.message || 'Fehler beim Abrufen der Regionen');
};

/**
 * Prüfen, ob aktuell Event-Regionen aktiv sind (für Sidebar-Indikator).
 */
export const getHasActiveEvents = async (): Promise<boolean> => {
  try {
    const { data } = await apiClient.get<{ success: boolean; data?: { hasActiveEvents: boolean } }>('/regions/active-events');
    return data?.success && data?.data?.hasActiveEvents === true;
  } catch {
    return false;
  }
};

/**
 * Region-Details abrufen
 */
export const getRegionById = async (id: string): Promise<Region> => {
  const { data } = await apiClient.get<{ success: boolean; data?: { region: Region }; error?: { message?: string } }>(`/regions/${id}`);
  if (data?.success && data?.data) return data.data.region;
  throw new Error(data?.error?.message || 'Fehler beim Abrufen der Region');
};

/**
 * Benutzer-Lizenzen abrufen
 */
export const getUserLicenses = async (userId: string): Promise<{ licenses: License[]; total: number }> => {
  const { data } = await apiClient.get<{ success: boolean; data?: { licenses: License[]; total: number }; error?: { message?: string } }>(`/regions/licenses/${userId}`);
  if (data?.success && data?.data) return data.data;
  throw new Error(data?.error?.message || 'Fehler beim Abrufen der Lizenzen');
};

/**
 * Region-Lizenz abrufen
 */
export const getRegionLicense = async (userId: string, regionId: string): Promise<License | null> => {
  const { data } = await apiClient.get<{ success: boolean; data?: { license: License | null }; error?: { message?: string } }>(`/regions/licenses/region/${regionId}/${userId}`);
  if (data?.success && data?.data !== undefined) return data.data.license;
  throw new Error(data?.error?.message || 'Fehler beim Abrufen der Region-Lizenz');
};

/**
 * Lizenz kaufen
 */
export const purchaseLicense = async (regionId: string): Promise<License> => {
  const { data } = await apiClient.post<{ success: boolean; data?: { license: License }; error?: { message?: string } }>('/regions/licenses/purchase', { region_id: regionId });
  if (data?.success && data?.data) return data.data.license;
  throw new Error(data?.error?.message || 'Fehler beim Kaufen der Lizenz');
};
